const React = require('react');
const { createElement: h, useState } = React;
const { HyperParamChips } = require('./WorkflowNode');
const VariableAutocompleteInput = require('./VariableAutocompleteInput');
const { getIncrementPreview } = require('../lib/incrementPreview');

// Renders a node's hyperparameters as a vertical list of labeled rows in the
// sidebar.
//
// For plain text/number params we render `VariableAutocompleteInput`, which
// drops its suggestion list + inline field tree DIRECTLY BELOW the input —
// no popup, no portal, no positioning math. That avoids the off-screen
// "CHOOSE VALUE" balloon that the chip-style editor produces in the sidebar.
//
// For specialized param types (boolean, datetime, array) we delegate to
// `HyperParamChips` with a single-param array, since those need their
// dedicated select / date / chip-list editors that already exist there.
//
// Rename + Add are INLINE — no native prompts, no popover overlays. Rename
// swaps the label text for an <input>; Add expands a small form at the
// bottom of the list. Both close on Esc / commit on Enter.

const ALLOWED_PARAM_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'multiline', label: 'Multiline text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' }
];

function isPlainTextParam(param) {
  var t = String(param && param.type || '').toLowerCase();
  if (t === 'boolean' || t === 'datetime' || t === 'array' || t === 'list') return false;
  return true;
}

// `'multiline'` is a plain-text param that renders as a textarea instead of
// a single-line input. Anything that opts in via `param.multiline === true`
// (legacy flag) is treated the same.
function isMultilineParam(param) {
  if (!param) return false;
  if (param.multiline === true) return true;
  return String(param.type || '').toLowerCase() === 'multiline';
}

function readParamValue(paramValues, param) {
  var raw = paramValues[param.key] !== undefined ? paramValues[param.key] : param.default;
  if (raw === null || raw === undefined) return '';
  if (Array.isArray(raw)) return raw.join(',');
  return String(raw);
}

// Inline-editable label. Click pencil → input takes over in place. Enter
// commits, Esc cancels, blur commits. No native prompt, no overlay.
function ParamLabel(props) {
  var label = props.label;
  var onRename = props.onRename; // (newLabel) => void
  var canRename = typeof onRename === 'function';
  var [editing, setEditing] = useState(false);
  var [draft, setDraft] = useState(label);

  function start() {
    setDraft(label);
    setEditing(true);
  }
  function cancel() { setEditing(false); }
  function commit() {
    var trimmed = String(draft || '').trim();
    setEditing(false);
    if (!trimmed || trimmed === label) return;
    onRename(trimmed);
  }
  function onKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancel(); }
  }

  if (editing) {
    return h('input', {
      className: 'wf-params-row-label-input',
      autoFocus: true,
      value: draft,
      onChange: function(e) { setDraft(e.target.value); },
      onBlur: commit,
      onKeyDown: onKeyDown
    });
  }

  return h('div', { className: 'wf-params-row-label-wrap' },
    h('div', { className: 'wf-params-row-label' }, label),
    canRename
      ? h('button', {
          type: 'button',
          className: 'wf-params-row-action',
          title: 'Rename',
          'aria-label': 'Rename param',
          onClick: start
        },
          h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
            h('path', { d: 'M12 20h9' }),
            h('path', { d: 'M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z' })
          )
        )
      : null
  );
}

function ParamRowActions(props) {
  var onRemove = props.onRemove;
  if (typeof onRemove !== 'function') return null;
  return h('div', { className: 'wf-params-row-actions' },
    h('button', {
      type: 'button',
      className: 'wf-params-row-action wf-params-row-action-danger',
      title: 'Delete this param',
      'aria-label': 'Delete param',
      onClick: onRemove
    },
      h('svg', { width: 12, height: 12, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2.2, strokeLinecap: 'round', strokeLinejoin: 'round' },
        h('polyline', { points: '3 6 5 6 21 6' }),
        h('path', { d: 'M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6' }),
        h('path', { d: 'M10 11v6' }),
        h('path', { d: 'M14 11v6' })
      )
    )
  );
}

function ParamRow(props) {
  var param = props.param;
  var paramValues = props.paramValues;
  var onParamChange = props.onParamChange;
  var onParamRename = props.onParamRename;
  var onParamRemove = props.onParamRemove;
  var variableNodes = props.variableNodes;
  var label = param.label || prettifyKey(param.key);
  var rawValue = readParamValue(paramValues, param);

  var control = isPlainTextParam(param)
    ? h(VariableAutocompleteInput, {
        value: rawValue,
        placeholder: param.placeholder || 'Type a value or @ to reference a variable',
        variableNodes: variableNodes,
        multiline: isMultilineParam(param),
        rows: param.rows,
        onChange: function(next) { onParamChange(param.key, next); }
      })
    : h(HyperParamChips, {
        params: [param],
        paramValues: paramValues,
        onParamChange: onParamChange,
        variableNodes: variableNodes
      });

  // N++ / N-- counter preview — same value the canvas chip shows, surfaced
  // here so the user sees what "1++" resolves to while the node is running.
  var incrPreview = getIncrementPreview(rawValue, props.nodeCallCount, props.nodeStatus);

  return h('div', { key: param.id || param.key, className: 'wf-params-row' },
    h('div', { className: 'wf-params-row-header' },
      // Numbered green badge (1..N over the visible params) — same visual
      // language as the Browser Action panel's stepped flow.
      typeof props.stepNumber === 'number'
        ? h('span', { className: 'wf-params-badge' }, props.stepNumber)
        : null,
      h(ParamLabel, {
        label: label,
        // The new key is derived from the label upstream (it needs the sibling
        // params to stay unique), so only the old key + new label travel here.
        onRename: onParamRename
          ? function(nextLabel) { onParamRename(param.key, nextLabel); }
          : null
      }),
      incrPreview !== null
        ? h('span', {
            className: 'wf-params-row-preview',
            title: 'Current value at runtime (from ' + rawValue + ')'
          },
            h('span', { className: 'wf-params-row-preview-arrow' }, '→'),
            incrPreview
          )
        : null,
      h(ParamRowActions, {
        onRemove: typeof onParamRemove === 'function'
          ? function() { onParamRemove(param.key); }
          : null
      })
    ),
    h('div', { className: 'wf-params-row-control' }, control),
    // Optional helper text under the field (e.g. "overrides connection input").
    param.hint ? h('div', { className: 'wf-params-row-hint' }, param.hint) : null
  );
}

// Inline "+ Add param" affordance. Collapsed → small button. Expanded →
// stacked Name / Type / Initial value fields with Add + Cancel buttons.
// Lives at the bottom of the params list; no overlay, no portal.
function AddParamRow(props) {
  var onAdd = props.onAdd; // ({name, type, initialValue}) => void
  if (typeof onAdd !== 'function') return null;

  var [open, setOpen] = useState(false);
  var [name, setName] = useState('');
  var [type, setType] = useState('text');
  var [initialValue, setInitialValue] = useState('');

  function reset() {
    setName('');
    setType('text');
    setInitialValue('');
  }
  function close() {
    setOpen(false);
    reset();
  }
  function submit() {
    var trimmed = String(name || '').trim();
    if (!trimmed) return;
    onAdd({ name: trimmed, type: type, initialValue: initialValue });
    close();
  }

  if (!open) {
    return h('button', {
      type: 'button',
      className: 'wf-params-add-btn',
      onClick: function() { setOpen(true); }
    },
      h('span', { className: 'wf-params-add-btn-plus' }, '+'),
      h('span', null, 'Add param')
    );
  }

  return h('div', { className: 'wf-params-add-form' },
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Name'),
      h('input', {
        className: 'wf-params-add-input',
        autoFocus: true,
        placeholder: 'e.g. API Key',
        value: name,
        onChange: function(e) { setName(e.target.value); }
      })
    ),
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Type'),
      h('select', {
        className: 'wf-params-add-input',
        value: type,
        onChange: function(e) { setType(e.target.value); }
      },
        ALLOWED_PARAM_TYPES.map(function(opt) {
          return h('option', { key: opt.value, value: opt.value }, opt.label);
        })
      )
    ),
    h('div', { className: 'wf-params-row' },
      h('div', { className: 'wf-params-row-label' }, 'Initial value'),
      h('input', {
        className: 'wf-params-add-input',
        placeholder: 'Optional',
        value: initialValue,
        onChange: function(e) { setInitialValue(e.target.value); }
      })
    ),
    h('div', { className: 'wf-params-add-actions' },
      h('button', {
        type: 'button',
        className: 'wf-params-add-cancel',
        onClick: close
      }, 'Cancel'),
      h('button', {
        type: 'button',
        className: 'wf-params-add-submit',
        onClick: submit,
        disabled: !String(name || '').trim()
      }, 'Add')
    )
  );
}

function NodeParamsForm(props) {
  var params = Array.isArray(props.params) ? props.params : [];
  var paramValues = props.paramValues || {};
  var onParamChange = props.onParamChange || function() {};
  var onParamRename = props.onParamRename || null;
  var onParamAdd = props.onParamAdd || null;
  var onParamRemove = props.onParamRemove || null;
  var variableNodes = props.variableNodes || [];
  var nodeStatus = props.nodeStatus || null;
  var nodeCallCount = props.nodeCallCount || 0;

  if (params.length === 0 && !onParamAdd) return null;

  return h('div', { className: 'wf-params-form' },
    params.map(function(param, i) {
      return h(ParamRow, {
        key: param.id || param.key,
        param: param,
        stepNumber: i + 1,
        paramValues: paramValues,
        onParamChange: onParamChange,
        onParamRename: onParamRename,
        onParamRemove: onParamRemove,
        variableNodes: variableNodes,
        nodeStatus: nodeStatus,
        nodeCallCount: nodeCallCount
      });
    }),
    onParamAdd ? h(AddParamRow, { onAdd: onParamAdd }) : null
  );
}

// Convert a param key like "userQuery" or "max_tokens" into a friendly label.
function prettifyKey(key) {
  var s = String(key || '');
  if (!s) return '';
  s = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  s = s.replace(/[_-]+/g, ' ');
  s = s.replace(/\b\w/g, function(c) { return c.toUpperCase(); });
  return s;
}

module.exports = NodeParamsForm;
